import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users, TrendingUp, Clock, CheckCircle2, XCircle, AlertCircle,
  RefreshCw, ChevronLeft, ChevronRight, Loader2, Search,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useAuth } from "../contexts/AuthContext";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FILTROS = [
  { key: "todos",           label: "Todos" },
  { key: "mensal",          label: "Mensal" },
  { key: "anual_essencial", label: "Anual Essencial" },
  { key: "anual_premium",   label: "Anual Premium" },
  { key: "trial",           label: "Trial" },
  { key: "pix_pendente",    label: "PIX Pendente" },
  { key: "expirados",       label: "Expirados" },
];

// Maps backend plano+plano_tipo to display label + badge color
function getPlanoBadge(plano, plano_tipo) {
  if (!plano || plano === "nenhum") return null;
  if (plano === "trial")     return { label: "Trial",           color: "bg-yellow-500/15 text-yellow-400 border-yellow-500/30" };
  if (plano === "familia")   return { label: "Família",         color: "bg-cyan-500/15 text-cyan-400 border-cyan-500/30" };
  if (plano === "mensal")    return { label: "Mensal",          color: "bg-blue-500/15 text-blue-400 border-blue-500/30" };
  if (plano === "essencial") return { label: "Anual Essencial", color: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" };
  if (plano === "premium")   return { label: "Anual Premium",   color: "bg-amber-500/15 text-amber-400 border-amber-500/30" };
  // legado
  if (plano === "basico")    return { label: "Mensal",          color: "bg-blue-500/15 text-blue-400 border-blue-500/30" };
  if (plano === "anual")     return { label: "Anual Premium",   color: "bg-amber-500/15 text-amber-400 border-amber-500/30" };
  if (plano === "vitalicio") return { label: "Anual Premium",   color: "bg-amber-500/15 text-amber-400 border-amber-500/30" };
  return { label: plano, color: "bg-slate-700 text-slate-300 border-slate-600" };
}

const fmt = (n = 0) =>
  n?.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const fmtDate = (d) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("pt-BR"); } catch { return d; }
};

export default function Admin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [accessChecked, setAccessChecked] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);
  const [accessError, setAccessError] = useState(null);

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/admin/stats`)
      .then(() => { setHasAccess(true); setAccessChecked(true); })
      .catch((err) => {
        const status = err.response?.status;
        const detail = err.response?.data?.detail || err.message;
        setAccessError(`HTTP ${status ?? "sem resposta"}: ${detail}`);
        setAccessChecked(true);
      });
  }, [user, navigate]);

  if (!user || !accessChecked) return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center">
      <Loader2 className="animate-spin text-emerald-500 h-8 w-8" />
    </div>
  );
  if (!hasAccess) return (
    <div className="min-h-screen bg-slate-950 text-white flex flex-col items-center justify-center gap-4">
      <p className="text-rose-400 font-semibold">Acesso negado ao painel admin</p>
      <p className="text-slate-400 text-sm font-mono">{accessError}</p>
      <p className="text-slate-500 text-xs">Email logado: {user?.email}</p>
      <button onClick={() => navigate("/app")} className="mt-2 text-emerald-400 underline text-sm">Voltar ao app</button>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <header className="border-b border-slate-800 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/app")} className="text-slate-400 hover:text-white transition-colors">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h1 className="font-display text-xl font-bold">Painel Admin</h1>
        </div>
        <span className="text-xs text-slate-500">Monyxx Finance</span>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-6 space-y-8">
        <StatsSection />
        <PixPendentesSection />
        <UsuariosSection />
      </div>
    </div>
  );
}

// ── Stats ────────────────────────────────────────────────────────────────────

function StatsSection() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/stats`);
      setStats(data);
    } catch { toast.error("Erro ao carregar stats."); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex justify-center py-6"><Loader2 className="animate-spin text-emerald-500" /></div>;
  if (!stats) return null;

  const cards = [
    { label: "Usuários totais",   value: stats.total,            icon: <Users className="w-5 h-5 text-blue-400" /> },
    { label: "Planos ativos",    value: stats.ativos,           icon: <CheckCircle2 className="w-5 h-5 text-emerald-400" /> },
    { label: "Mensal",           value: stats.mensal,           icon: <Clock className="w-5 h-5 text-blue-400" /> },
    { label: "Anual Essencial",  value: stats.anual_essencial,  icon: <TrendingUp className="w-5 h-5 text-emerald-400" /> },
    { label: "Anual Premium",    value: stats.anual_premium,    icon: <CheckCircle2 className="w-5 h-5 text-amber-400" /> },
    { label: "Trial",            value: stats.trial,            icon: <AlertCircle className="w-5 h-5 text-yellow-400" /> },
    { label: "Expirados",        value: stats.expirados,        icon: <XCircle className="w-5 h-5 text-red-400" /> },
    { label: "PIX pendentes",    value: stats.pix_pendente,     icon: <AlertCircle className="w-5 h-5 text-orange-400" /> },
  ];

  return (
    <section>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-lg font-bold">Visão Geral</h2>
        <Button variant="ghost" size="sm" onClick={load} className="text-slate-400 hover:text-white gap-1">
          <RefreshCw className="w-4 h-4" /> Atualizar
        </Button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        {cards.map((c) => (
          <div key={c.label} className="rounded-xl bg-slate-900 border border-slate-800 p-4">
            <div className="flex items-center gap-2 mb-1">{c.icon}<span className="text-xs text-slate-400">{c.label}</span></div>
            <div className="font-display text-2xl font-bold">{c.value ?? 0}</div>
          </div>
        ))}
      </div>
      <div className="rounded-xl bg-slate-900 border border-slate-800 p-4 flex items-center gap-4">
        <TrendingUp className="w-6 h-6 text-emerald-400" />
        <div>
          <p className="text-xs text-slate-400">Receita estimada (planos pagos)</p>
          <p className="font-display text-xl font-bold text-emerald-400">{fmt(stats.receita_estimada)}</p>
        </div>
      </div>
    </section>
  );
}

// ── PIX Pendentes ────────────────────────────────────────────────────────────

function PixPendentesSection() {
  const [items, setItems]   = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/pix-pendentes`);
      setItems(data.items || []);
    } catch { /* silently skip if none */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const confirmar = async (pix_id, plano) => {
    try {
      await axios.post(`${API}/admin/pix-confirmar`, { pix_id, plano });
      toast.success("PIX confirmado e plano ativado!");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao confirmar PIX.");
    }
  };

  const rejeitar = async (pix_id) => {
    const motivo = prompt("Motivo da rejeição (opcional):") || "";
    try {
      await axios.post(`${API}/admin/pix-rejeitar`, { pix_id, motivo });
      toast.success("PIX rejeitado.");
      load();
    } catch { toast.error("Erro ao rejeitar."); }
  };

  if (loading) return null;
  if (!items.length) return null;

  const PLANO_MAP = {
    "mensal_mensal":    "Plano Mensal",
    "essencial_anual":  "Plano Anual Essencial",
    "premium_anual":    "Plano Anual Premium",
    "basico_anual":     "Plano Mensal",
    "premium_vitalicio":"Plano Anual Premium",
  };

  return (
    <section>
      <h2 className="font-display text-lg font-bold mb-4 flex items-center gap-2">
        <AlertCircle className="w-5 h-5 text-orange-400" /> PIX Pendentes ({items.length})
      </h2>
      <div className="space-y-3">
        {items.map((item) => (
          <div key={item.pix_id} className="rounded-xl bg-slate-900 border border-orange-500/30 p-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-white text-sm truncate">{item.nome} — {item.email}</p>
                <p className="text-xs text-slate-400 mt-0.5">
                  Plano solicitado: <span className="text-slate-200">{PLANO_MAP[item.plano] || item.plano}</span>
                  {" · "}Recebido: {fmtDate(item.created_at)}
                </p>
                {item.comprovante && (
                  <p className="text-xs font-mono text-slate-500 mt-1 truncate max-w-xs">{item.comprovante}</p>
                )}
              </div>
              <div className="flex gap-2 shrink-0">
                <Button
                  size="sm"
                  onClick={() => confirmar(item.pix_id, item.plano)}
                  className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold text-xs"
                >
                  <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Confirmar
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => rejeitar(item.pix_id)}
                  className="border-red-500/50 text-red-400 hover:bg-red-500/10 text-xs"
                >
                  <XCircle className="w-3.5 h-3.5 mr-1" /> Rejeitar
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ── Usuários ─────────────────────────────────────────────────────────────────

function UsuariosSection() {
  const [filtro, setFiltro]   = useState("todos");
  const [page, setPage]       = useState(1);
  const [data, setData]       = useState({ users: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [search, setSearch]   = useState("");
  const [ativar, setAtivar]   = useState(null);

  const limit = 20;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/admin/usuarios`, {
        params: { filtro, page, limit },
      });
      setData(d);
    } catch { toast.error("Erro ao carregar usuários."); }
    finally { setLoading(false); }
  }, [filtro, page]);

  useEffect(() => { setPage(1); }, [filtro]);
  useEffect(() => { load(); }, [load]);

  const users = data.users.filter((u) =>
    !search ||
    u.email?.toLowerCase().includes(search.toLowerCase()) ||
    u.name?.toLowerCase().includes(search.toLowerCase())
  );

  const revogar = async (u) => {
    if (!window.confirm(`Revogar plano de ${u.email}?`)) return;
    try {
      await axios.post(`${API}/admin/revogar-plano`, { email: u.email });
      toast.success(`Plano revogado para ${u.email}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao revogar plano.");
    }
  };

  const totalPages = Math.ceil(data.total / limit);

  const isAtivo = (u) => {
    if (!u) return false;
    if (u.role === "superadmin") return true;
    if (u.plano_status === "ativo") return true;
    if (u.plano === "familia") return true;
    if (u.plano === "anual" || u.plano === "vitalicio") return true;
    return false;
  };

  const getExpira = (u) => {
    if (!u) return "—";
    if (u.plano === "familia") return "—";
    if (u.plano_expira_em) return fmtDate(u.plano_expira_em);
    if (u.trial_expira_em && u.plano === "trial") return fmtDate(u.trial_expira_em);
    return "—";
  };

  return (
    <section>
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
        <h2 className="font-display text-lg font-bold">Usuários</h2>
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nome ou e-mail…"
            className="pl-9 bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 text-sm"
          />
        </div>
        <Button variant="ghost" size="sm" onClick={load} className="text-slate-400 hover:text-white gap-1 shrink-0">
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* Filter tabs */}
      <div className="flex flex-wrap gap-2 mb-4">
        {FILTROS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFiltro(f.key)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              filtro === f.key
                ? "bg-emerald-500 text-slate-950"
                : "bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Activate modal */}
      {ativar && (
        <AtivarPlanoModal
          user={ativar}
          onClose={() => setAtivar(null)}
          onSuccess={() => { setAtivar(null); load(); }}
        />
      )}

      {/* Table */}
      <div className="rounded-xl border border-slate-800 overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="animate-spin text-emerald-500" /></div>
        ) : users.length === 0 ? (
          <div className="text-center py-10 text-slate-500 text-sm">Nenhum usuário encontrado.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-900 border-b border-slate-800">
                <tr>
                  {["Nome", "E-mail", "Plano", "Status", "Expira em", ""].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wide whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.map((u) => {
                  const badge = getPlanoBadge(u.plano, u.plano_tipo);
                  const ativo = isAtivo(u);
                  return (
                    <tr key={u.user_id || u.email} className="border-b border-slate-800/50 hover:bg-slate-900/40 transition-colors">
                      <td className="px-4 py-3 font-medium text-white">{u.name || "—"}</td>
                      <td className="px-4 py-3 text-slate-400 truncate max-w-[180px]">{u.email}</td>
                      <td className="px-4 py-3">
                        {badge ? (
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${badge.color}`}>
                            {badge.label}
                          </span>
                        ) : (
                          <span className="text-slate-600 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span
                            className={`inline-block w-9 h-5 rounded-full relative transition-colors ${
                              ativo ? "bg-emerald-500" : "bg-slate-700"
                            }`}
                          >
                            <span
                              className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                                ativo ? "translate-x-4" : "translate-x-0.5"
                              }`}
                            />
                          </span>
                          <span className={`text-xs ${ativo ? "text-emerald-400" : "text-slate-500"}`}>
                            {ativo ? "Ativo" : "Inativo"}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-400 whitespace-nowrap text-xs">{getExpira(u)}</td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setAtivar(u)}
                            className="text-xs text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10"
                          >
                            Ativar
                          </Button>
                          {ativo && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => revogar(u)}
                              className="text-xs text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            >
                              Revogar
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-xs text-slate-500">{data.total} usuário(s) · página {page}/{totalPages}</p>
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" disabled={page === 1} onClick={() => setPage((p) => p - 1)} className="text-slate-400">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button size="sm" variant="ghost" disabled={page === totalPages} onClick={() => setPage((p) => p + 1)} className="text-slate-400">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}

// ── Ativar Plano Modal ───────────────────────────────────────────────────────

const PLANO_OPCOES = [
  { label: "Mensal",          desc: "Expira em 1 mês — R$ 14,90",                   plano: "mensal",    plano_tipo: "mensal" },
  { label: "Anual Essencial", desc: "Expira em 1 ano — R$ 99,90",                   plano: "essencial", plano_tipo: "anual"  },
  { label: "Anual Premium",   desc: "Expira em 1 ano + Família incluso — R$ 139,90", plano: "premium",   plano_tipo: "anual"  },
];

function AtivarPlanoModal({ user, onClose, onSuccess }) {
  const [opcao, setOpcao]         = useState(PLANO_OPCOES[0].label);
  const [dataInicio, setDataInicio] = useState(() => new Date().toISOString().slice(0, 10));
  const [loading, setLoading]     = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const escolha = PLANO_OPCOES.find((o) => o.label === opcao) || PLANO_OPCOES[0];
    setLoading(true);
    try {
      await axios.post(`${API}/admin/ativar-plano`, {
        email:      user.email,
        plano:      escolha.plano,
        plano_tipo: escolha.plano_tipo,
        data_inicio: dataInicio,
      });
      toast.success(`Plano "${opcao}" ativado para ${user.email}`);
      onSuccess();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao ativar plano.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-slate-900 border border-slate-700 rounded-2xl p-6 w-full max-w-sm space-y-5"
      >
        <div>
          <h3 className="font-display text-lg font-bold">Ativar Plano Manual</h3>
          <p className="text-sm text-slate-400 mt-1 truncate">{user.email}</p>
        </div>

        {/* Plano cards */}
        <div>
          <label className="text-xs text-slate-400 font-semibold uppercase tracking-wide block mb-2">Plano</label>
          <div className="flex flex-col gap-2">
            {PLANO_OPCOES.map((o) => (
              <button
                type="button"
                key={o.label}
                onClick={() => setOpcao(o.label)}
                className={`py-2.5 px-3 rounded-xl border-2 transition-all text-left ${
                  opcao === o.label
                    ? "border-emerald-500 bg-emerald-500/10"
                    : "border-slate-700 bg-slate-800 hover:border-slate-500"
                }`}
              >
                <p className={`text-sm font-semibold ${opcao === o.label ? "text-emerald-400" : "text-white"}`}>{o.label}</p>
                <p className="text-xs text-slate-500 mt-0.5">{o.desc}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Data de início */}
        <div>
          <label className="text-xs text-slate-400 font-semibold uppercase tracking-wide block mb-2">Data de início</label>
          <input
            type="date"
            value={dataInicio}
            onChange={(e) => setDataInicio(e.target.value)}
            className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-emerald-500 [color-scheme:dark]"
          />
        </div>

        <div className="flex gap-3 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-2.5 rounded-lg text-sm font-medium bg-slate-800 text-slate-400 hover:bg-slate-700 hover:text-white transition-colors"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={loading}
            className="flex-1 py-2.5 rounded-lg text-sm font-bold bg-emerald-500 hover:bg-emerald-400 text-slate-950 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            Confirmar ativação
          </button>
        </div>
      </form>
    </div>
  );
}
